import { useMemo } from 'react'

import { isPast } from 'date-fns'

import { isUnclosedSessionStatus } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/constants'
import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { CLOSE_SESSION_TYPES } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/types'
import { getCloseSessionType } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { Note, NoteStatusStates } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'

import { DAEngageCloseSessionType, ProviderTaskItem } from '../tasksModule/types'

const isDAEngageTaskType = (type: CLOSE_SESSION_TYPES): type is DAEngageCloseSessionType =>
  type === CLOSE_SESSION_TYPES.INVOICE_NEEDED ||
  type === CLOSE_SESSION_TYPES.INVOICE_AND_NOTE_NEEDED ||
  type === CLOSE_SESSION_TYPES.NOTE_NEEDED

export const getTaskEligibleAppointments = (
  allAppointments: Appointment[],
  selectedEpisodeId: string | undefined,
  providerId: string,
): Appointment[] => {
  if (!selectedEpisodeId) {
    return []
  }

  return allAppointments.filter(
    (appointment) => appointment.episodeId === selectedEpisodeId && appointment.lyraProviderId === providerId,
  )
}

export const useClientTasks = (
  allAppointments: Appointment[],
  sessionsNeedingNotes: number[],
  selectedEpisodeId: string | undefined,
  episodeNotes: Note[],
  isDAEngageProvider: boolean,
  providerId: string,
): ProviderTaskItem[] => {
  return useMemo(() => {
    if (!selectedEpisodeId) {
      return []
    }

    const episodeAppointments = getTaskEligibleAppointments(allAppointments, selectedEpisodeId, providerId)

    const existingDraftNoteId = (appointment: Appointment) =>
      episodeNotes.find(
        (note) => note.appointment_id === appointment.appointmentId && note.status === NoteStatusStates.DRAFT,
      )?.id

    const buildDAEngageTasks = (): ProviderTaskItem[] => {
      // Missed/canceled sessions never owe any action
      const actionableEpisodeAppointments = episodeAppointments.filter(
        (appointment) =>
          isPast(getDateFromAppointment(appointment)) &&
          appointment.appointmentStatus !== 'missed' &&
          appointment.appointmentStatus !== 'canceled',
      )

      const tasks: ProviderTaskItem[] = []

      for (const appointment of actionableEpisodeAppointments) {
        const sessionNeedsNote = sessionsNeedingNotes.includes(appointment.sessionNumber)
        const isLegacyDAAppointment = appointment.programId == null

        const closeSessionType = getCloseSessionType({
          appointmentStatus: appointment.appointmentStatus,
          isDAEngageProvider: true,
          isInPast: true,
          sessionNeedsNote,
          isLegacyDAAppointment,
          isChargeReversed: !!appointment.isChargeReversed,
        })

        if (isDAEngageTaskType(closeSessionType)) {
          tasks.push({
            kind: 'daEngageTask',
            appointment,
            episodeId: selectedEpisodeId,
            closeSessionType,
          })
          continue
        }

        // A legacy DA appointment (no `programId`) can't be invoiced, so `getCloseSessionType` always
        // resolves it to NONE. It can still need a note, though.
        if (isLegacyDAAppointment && sessionNeedsNote) {
          tasks.push({
            kind: 'completeNote',
            appointment,
            episodeId: selectedEpisodeId,
            existingNoteId: existingDraftNoteId(appointment),
          })
        }
      }

      return tasks
    }

    const buildNonDAEngageTasks = (): ProviderTaskItem[] => {
      // The same rule Care Timeline and the Open Items dashboard apply: a
      // reversed charge re-opens an already-closed session.
      const closeSessionTasks: ProviderTaskItem[] = episodeAppointments
        .filter(
          (appointment) =>
            isPast(getDateFromAppointment(appointment)) &&
            (isUnclosedSessionStatus(appointment.appointmentStatus) || !!appointment.isChargeReversed),
        )
        .map((appointment) => ({
          kind: 'closeSession',
          appointment,
          episodeId: selectedEpisodeId,
        }))

      const completeNoteTasks: ProviderTaskItem[] = episodeAppointments
        .filter((appointment) => sessionsNeedingNotes.includes(appointment.sessionNumber))
        .map((appointment) => ({
          kind: 'completeNote',
          appointment,
          episodeId: selectedEpisodeId,
          existingNoteId: existingDraftNoteId(appointment),
        }))

      return [...closeSessionTasks, ...completeNoteTasks]
    }

    const tasks = isDAEngageProvider ? buildDAEngageTasks() : buildNonDAEngageTasks()

    return tasks.sort(
      (a, b) => getDateFromAppointment(a.appointment).getTime() - getDateFromAppointment(b.appointment).getTime(),
    )
  }, [allAppointments, episodeNotes, isDAEngageProvider, providerId, sessionsNeedingNotes, selectedEpisodeId])
}
